import { useState } from 'react';
import { X } from 'lucide-react';
import type { PageMedia, PageSection, SectionWidth, TileItem } from '../../types';

const isExternalLink = (value: string | undefined): value is string =>
  typeof value === 'string' && /^https?:\/\//i.test(value.trim());

/**
 * Classes Tailwind statiques et complètes pour chaque largeur : le JIT de
 * Tailwind v4 scanne le code source à la recherche de noms de classes
 * littéraux. Une classe construite dynamiquement (`w-${...}`) ne serait pas
 * détectée et n'apparaîtrait dans aucune feuille de style compilée.
 */
const WIDTH_CLASS: Record<SectionWidth, string> = {
  '1/4': 'w-full md:w-1/4',
  '1/3': 'w-full md:w-1/3',
  '1/2': 'w-full md:w-1/2',
  full: 'w-full',
};

function MediaView({ media, className = '' }: { media: PageMedia; className?: string }) {
  if (media.kind === 'video') {
    return (
      <video
        src={media.url}
        controls
        className={`w-full h-full object-cover ${className}`}
        aria-label={media.alt}
      />
    );
  }
  return (
    <img
      src={media.url}
      alt={media.alt || ''}
      className={`w-full h-full object-cover ${className}`}
      loading="lazy"
    />
  );
}

function TextSectionView({ section }: { section: Extract<PageSection, { type: 'text' }> }) {
  const alignClass =
    section.align === 'center' ? 'text-center' : section.align === 'right' ? 'text-right' : 'text-left';
  return (
    <div className={`px-4 py-6 ${alignClass}`}>
      {section.heading && <h2 className="text-2xl font-bold mb-3">{section.heading}</h2>}
      <p className="whitespace-pre-line text-gray-700 leading-relaxed">{section.body}</p>
    </div>
  );
}

function ImageSectionView({ section }: { section: Extract<PageSection, { type: 'image' }> }) {
  const content = <MediaView media={section.media} className="rounded-lg" />;
  return (
    <div className="px-4 py-6">
      {section.link ? (
        <a href={section.link} target="_blank" rel="noreferrer noopener" className="block">
          {content}
        </a>
      ) : (
        content
      )}
      {section.media.caption && (
        <p className="mt-2 text-sm text-gray-500 text-center">{section.media.caption}</p>
      )}
    </div>
  );
}

function SliderSectionView({ section }: { section: Extract<PageSection, { type: 'slider' }> }) {
  const [index, setIndex] = useState(0);
  const item = section.items[index];
  if (!item) return null;

  return (
    <div className="px-4 py-6">
      <div className="relative aspect-video rounded-lg overflow-hidden bg-gray-100">
        <MediaView media={item} />
        {section.items.length > 1 && (
          <>
            <button
              type="button"
              aria-label="Média précédent"
              onClick={() => setIndex((i) => (i - 1 + section.items.length) % section.items.length)}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full w-9 h-9 flex items-center justify-center shadow"
            >
              ‹
            </button>
            <button
              type="button"
              aria-label="Média suivant"
              onClick={() => setIndex((i) => (i + 1) % section.items.length)}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full w-9 h-9 flex items-center justify-center shadow"
            >
              ›
            </button>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
              {section.items.map((mediaItem, i) => (
                <button
                  key={`${mediaItem.url}-${i}`}
                  type="button"
                  aria-label={`Aller au média ${i + 1}`}
                  onClick={() => setIndex(i)}
                  className={`w-2 h-2 rounded-full ${i === index ? 'bg-white' : 'bg-white/50'}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
      {item.caption && <p className="mt-2 text-sm text-gray-500 text-center">{item.caption}</p>}
    </div>
  );
}

function TileDetailModal({ item, onClose }: { item: TileItem; onClose: () => void }) {
  const hasBubbles = item.priceFromValue || item.peopleFromValue;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={item.title}
    >
      <div
        className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between gap-4 z-10">
          <h3 className="text-lg font-bold text-gray-900">{item.title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="shrink-0 text-gray-400 hover:text-gray-700 cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {hasBubbles && (
            <div className="flex flex-wrap gap-2">
              {item.priceFromValue && (
                <span className="inline-flex flex-col px-3 py-1.5 rounded-full bg-gray-100 text-xs">
                  <span className="text-[10px] uppercase tracking-wide text-gray-500">
                    {item.priceFromLabel || 'À partir de'}
                  </span>
                  <span className="font-bold text-gray-900">{item.priceFromValue}</span>
                </span>
              )}
              {item.peopleFromValue && (
                <span className="inline-flex flex-col px-3 py-1.5 rounded-full bg-gray-100 text-xs">
                  <span className="text-[10px] uppercase tracking-wide text-gray-500">
                    {item.peopleFromLabel || 'À partir de'}
                  </span>
                  <span className="font-bold text-gray-900">{item.peopleFromValue}</span>
                </span>
              )}
            </div>
          )}

          {item.image && (
            <img
              src={item.image}
              alt={item.title}
              style={{ objectPosition: item.imagePosition || '50% 50%' }}
              className="w-full aspect-video object-cover rounded-lg"
            />
          )}

          {item.detailVideoUrl && (
            <video src={item.detailVideoUrl} controls className="w-full rounded-lg" />
          )}

          {item.detailText && (
            <p className="whitespace-pre-line text-gray-700 leading-relaxed text-sm">
              {item.detailText}
            </p>
          )}

          {item.gallery && item.gallery.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {item.gallery.map((media, i) => (
                <div key={i} className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                  <MediaView media={media} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TileGridSectionView({ section }: { section: Extract<PageSection, { type: 'tileGrid' }> }) {
  const [openItem, setOpenItem] = useState<TileItem | null>(null);

  return (
    <div className="px-4 py-6 w-full">
      {(section.heading || section.subheading) && (
        <div className="text-center mb-8 space-y-1">
          {section.subheading && (
            <span className="block text-xs font-bold uppercase tracking-[0.2em] text-gray-400">
              {section.subheading}
            </span>
          )}
          {section.heading && <h2 className="text-2xl font-bold text-gray-900">{section.heading}</h2>}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {section.items.map((item) => {
          const link = item.link?.trim();
          const hasDetail = Boolean(
            item.detailText ||
              item.detailVideoUrl ||
              item.priceFromValue ||
              item.peopleFromValue ||
              (item.gallery && item.gallery.length > 0)
          );
          const tileClass =
            'group text-left bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-md transition-shadow flex flex-col';

          const content = (
            <>
              {item.image && (
                <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                  <img
                    src={item.image}
                    alt={item.title}
                    style={{ objectPosition: item.imagePosition || '50% 50%' }}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
              )}
              <div className="p-4 space-y-1.5 flex-1">
                <h3 className="font-bold text-sm text-gray-900">{item.title}</h3>
                {item.teaser && <p className="text-xs text-gray-500 leading-relaxed">{item.teaser}</p>}
                {(item.priceFromValue || item.peopleFromValue) && (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {item.priceFromValue && (
                      <span className="inline-flex flex-col px-2 py-1 rounded-md bg-gray-100 text-[10px] leading-tight">
                        <span className="text-gray-500">{item.priceFromLabel || 'À partir de'}</span>
                        <span className="font-bold text-gray-900">{item.priceFromValue}</span>
                      </span>
                    )}
                    {item.peopleFromValue && (
                      <span className="inline-flex flex-col px-2 py-1 rounded-md bg-gray-100 text-[10px] leading-tight">
                        <span className="text-gray-500">{item.peopleFromLabel || 'À partir de'}</span>
                        <span className="font-bold text-gray-900">{item.peopleFromValue}</span>
                      </span>
                    )}
                  </div>
                )}
              </div>
            </>
          );

          if (link) {
            return (
              <a
                key={item.id}
                href={link}
                {...(isExternalLink(link) ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                className={tileClass}
              >
                {content}
              </a>
            );
          }
          if (hasDetail) {
            return (
              <button key={item.id} type="button" onClick={() => setOpenItem(item)} className={tileClass}>
                {content}
              </button>
            );
          }
          return (
            <div key={item.id} className={tileClass}>
              {content}
            </div>
          );
        })}
      </div>

      {openItem && <TileDetailModal item={openItem} onClose={() => setOpenItem(null)} />}
    </div>
  );
}

function SeparatorSectionView({ section }: { section: Extract<PageSection, { type: 'separator' }> }) {
  if (section.style === 'space') {
    return <div className="w-full h-12" aria-hidden="true" />;
  }
  return (
    <div className="w-full px-4 py-6">
      <hr className="border-t border-gray-200" />
    </div>
  );
}

export function SectionRenderer({ section }: { section: PageSection }) {
  return (
    <div className={WIDTH_CLASS[section.width]}>
      {section.type === 'text' && <TextSectionView section={section} />}
      {section.type === 'image' && <ImageSectionView section={section} />}
      {section.type === 'slider' && <SliderSectionView section={section} />}
      {section.type === 'tileGrid' && <TileGridSectionView section={section} />}
      {section.type === 'separator' && <SeparatorSectionView section={section} />}
    </div>
  );
}
